import { useState } from 'react';

/**
 * LegalModal — displays Terms of Service, Privacy Policy, or Safety Disclaimer
 * Usage: <LegalModal type="terms" onClose={...} onAccept={...} />
 *
 * type: "terms" | "privacy" | "safety" | "events"
 * onAccept: optional, shows "I Agree" button if provided
 * onClose: always available
 */

const LEGAL_VERSION = '2026-02-12';

export { LEGAL_VERSION };

export default function LegalModal({ type, onClose, onAccept }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const doc = DOCUMENTS[type] || DOCUMENTS.terms;

  function handleScroll(e) {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolledToBottom(true);
    }
  }

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 700, margin: 0 }}>{doc.title}</h2>
            <p style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', margin: '2px 0 0' }}>
              Version {LEGAL_VERSION} — Proper Miles Inc.
            </p>
          </div>
          <button onClick={onClose} style={closeBtnStyle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div
          onScroll={handleScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 20px',
            fontSize: '0.8125rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
          }}
        >
          {doc.sections.map((section, i) => (
            <div key={i} style={{ marginBottom: 20 }}>
              {section.heading && (
                <h3 style={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}>
                  {section.heading}
                </h3>
              )}
              {section.paragraphs.map((p, j) => (
                <p key={j} style={{ marginBottom: 8 }}>{p}</p>
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px 16px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: 8,
        }}>
          {onAccept && (
            <button
              onClick={onAccept}
              disabled={!scrolledToBottom && doc.sections.length > 3}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: (scrolledToBottom || doc.sections.length <= 3) ? 'var(--accent)' : 'var(--bg-elevated)',
                color: (scrolledToBottom || doc.sections.length <= 3) ? 'white' : 'var(--text-muted)',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: (scrolledToBottom || doc.sections.length <= 3) ? 'pointer' : 'default',
                transition: 'all 0.15s',
              }}
            >
              I Agree
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              flex: onAccept ? 0 : 1,
              padding: '12px 16px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
              minWidth: onAccept ? 80 : undefined,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Styles ─── */

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const modalStyle = {
  width: '100%',
  maxWidth: 520,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-primary)',
  borderRadius: 'var(--radius-lg, 16px)',
  overflow: 'hidden',
};

const closeBtnStyle = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: 'var(--bg-elevated)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-muted)',
  flexShrink: 0,
};

/* ─── Legal Documents ─── */

const DOCUMENTS = {
  terms: {
    title: 'Terms of Service',
    sections: [
      {
        heading: '1. Acceptance of Terms',
        paragraphs: [
          'By accessing or using the Proper Miles Mobile Garage application ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.',
          'Proper Miles Inc. ("we", "us", "our") reserves the right to update these Terms at any time. Continued use of the Service after changes constitutes acceptance of the updated Terms.',
        ],
      },
      {
        heading: '2. Service Description',
        paragraphs: [
          'Proper Miles Mobile Garage is a vehicle maintenance tracking and advisory tool that uses artificial intelligence to provide maintenance recommendations, cost estimates, DIY repair guides, and vehicle market valuations.',
          'The Service is provided for informational and educational purposes only. All AI-generated content, including maintenance schedules, repair instructions, cost estimates, and vehicle valuations, are estimates based on general automotive data and should not be relied upon as professional mechanical advice.',
        ],
      },
      {
        heading: '3. Disclaimer of Warranties',
        paragraphs: [
          'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
          'We do not warrant that: (a) the Service will meet your requirements; (b) the Service will be uninterrupted, timely, secure, or error-free; (c) any AI-generated content, including repair guides, maintenance recommendations, or cost estimates, will be accurate, reliable, or complete.',
        ],
      },
      {
        heading: '4. Limitation of Liability',
        paragraphs: [
          'TO THE MAXIMUM EXTENT PERMITTED BY LAW, PROPER MILES INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO PERSONAL INJURY, PROPERTY DAMAGE, LOSS OF USE, LOSS OF DATA, OR LOSS OF PROFITS, ARISING FROM YOUR USE OF THE SERVICE.',
          'This limitation applies regardless of whether the damages are based on warranty, contract, tort, negligence, strict liability, or any other legal theory, even if we have been advised of the possibility of such damages.',
        ],
      },
      {
        heading: '5. User Responsibilities',
        paragraphs: [
          'You are solely responsible for: (a) verifying all information provided by the Service before acting on it; (b) ensuring all vehicle repairs are performed safely and correctly; (c) consulting a qualified mechanic for any repair you are not confident performing; (d) maintaining the accuracy of your vehicle and account information.',
          'You acknowledge that vehicle repair involves inherent risks including personal injury and property damage. You assume all risks associated with performing any repairs suggested by the Service.',
        ],
      },
      {
        heading: '6. Subscription and Payments',
        paragraphs: [
          'Certain features of the Service require a paid subscription. Subscription fees are billed in advance on a monthly basis. You may cancel your subscription at any time, effective at the end of the current billing period.',
          'AI credits are allocated per billing period and do not roll over. Refunds are not provided for unused credits or partial billing periods.',
        ],
      },
      {
        heading: '7. Intellectual Property',
        paragraphs: [
          'All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are owned by Proper Miles Inc. and are protected by copyright, trademark, and other intellectual property laws.',
          'AI-generated content provided to you through the Service is licensed for your personal, non-commercial use only. You may not redistribute, sell, or commercially exploit AI-generated repair guides or analyses.',
        ],
      },
      {
        heading: '8. Governing Law',
        paragraphs: [
          'These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions.',
          'Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.',
          'If you are a consumer based in the United Kingdom or the European Economic Area (EEA), nothing in this section limits your mandatory consumer-protection rights under the laws of your country of residence, which apply to you in addition to (and where applicable, in place of) the provisions above. You may also bring proceedings in the courts of your country of residence.',
        ],
      },
      {
        heading: '9. Contact',
        paragraphs: [
          'For questions about these Terms, contact us at legal@propermiles.com.',
        ],
      },
    ],
  },

  privacy: {
    title: 'Privacy Policy',
    sections: [
      {
        heading: '1. Information We Collect',
        paragraphs: [
          'We collect information you provide directly: name, email address, ZIP code, vehicle information (year, make, model, mileage, VIN), service history, and photos you upload.',
          'We automatically collect usage data including: features accessed, AI queries made, device type, browser type, and general location derived from your ZIP code. With your permission, we may access your device location to auto-detect your ZIP code. We do not store GPS coordinates — only the derived ZIP code is saved.',
        ],
      },
      {
        heading: '2. How We Use Your Information',
        paragraphs: [
          'We use your information to: (a) provide and improve the Service; (b) generate personalized maintenance recommendations and cost estimates; (c) send service reminders and alerts you have opted into; (d) analyze aggregate usage patterns to improve our AI models.',
          'Vehicle data and service history you enter is used to provide personalized recommendations. AI-generated content is created based on your specific vehicle information.',
        ],
      },
      {
        heading: '3. Data Storage and Security',
        paragraphs: [
          'Your vehicle data and preferences are stored locally on your device using browser localStorage. We do not store your vehicle data on our servers except temporarily during AI analysis processing.',
          'AI queries are processed through OpenAI\'s API. Vehicle information sent for analysis is subject to OpenAI\'s data usage policies. We do not send your name, email, or payment information to AI providers.',
        ],
      },
      {
        heading: '4. Data Sharing',
        paragraphs: [
          'We do not sell your personal information to third parties. We may share anonymized, aggregate data for research purposes. We may share data as required by law or to protect our legal rights.',
          'If you use the garage sharing feature, vehicle data you choose to share will be visible to your designated collaborators.',
        ],
      },
      {
        heading: '5. Your Rights',
        paragraphs: [
          'You may: (a) access your data at any time through the app; (b) export your service history as CSV; (c) delete all your data using the Reset function in Settings; (d) opt out of non-essential communications.',
          'California residents have additional rights under the CCPA. Contact privacy@propermiles.com for CCPA-specific requests.',
          'If you are based in the United Kingdom or the European Economic Area, see Section 6 below for your rights under the UK Data Protection Act 2018 and the EU General Data Protection Regulation (GDPR).',
        ],
      },
      {
        heading: '6. UK & EEA Users (GDPR / UK DPA)',
        paragraphs: [
          'This section applies if you are based in the United Kingdom or the European Economic Area. It supplements the rest of this Privacy Policy and prevails to the extent of any conflict.',
          'Controller. Proper Miles Inc. (“Proper Miles”) is the controller of personal data we process about you in connection with the Service. You can contact us about any privacy matter at privacy@propermiles.com.',
          'Lawful basis for processing. We process your personal data based on: (a) the performance of our contract with you to provide the Service (Article 6(1)(b) GDPR); (b) your explicit consent where required, including for optional analytics cookies and live-location features (Article 6(1)(a)); and (c) our legitimate interests in keeping the Service secure, preventing fraud, and improving its quality (Article 6(1)(f)).',
          'International transfers. The Service is operated from the United States. When your personal data is transferred from the UK or EEA to the United States or other countries outside your jurisdiction, we rely on the European Commission\'s Standard Contractual Clauses (and the UK International Data Transfer Addendum where applicable) to provide appropriate safeguards. A copy is available on request from privacy@propermiles.com.',
          'Your rights. Under the GDPR and UK DPA you have the right to: (a) access the personal data we hold about you; (b) request correction of inaccurate or incomplete data; (c) request erasure of your data ("right to be forgotten"); (d) request that we restrict our processing of your data; (e) object to processing based on legitimate interests, including profiling, and to direct marketing; (f) receive your data in a portable, machine-readable format and have it transmitted to another controller where technically feasible; and (g) withdraw any consent you have given, at any time, without affecting the lawfulness of processing carried out before withdrawal.',
          'How to exercise your rights. Use the in-app Settings → Reset to delete your data, the in-app Export feature for portability, and contact privacy@propermiles.com for any other request. We will respond within one month.',
          'Right to complain. You have the right to lodge a complaint with your local data protection authority. In the UK, the Information Commissioner\'s Office (ico.org.uk). In the EEA, the supervisory authority of your country of residence. We would appreciate the chance to address your concerns first — please contact us at privacy@propermiles.com before filing a complaint where practical.',
          'Retention. We keep your account and vehicle data for as long as your account is active. On account deletion, we remove your personal data from our active systems within 30 days; backups are purged on a rolling 90-day cycle. Aggregated, anonymized data may be retained for analytics.',
          'Children. The Service is not directed at children under 16 and we do not knowingly process the personal data of children under 16. If you believe a child has provided us data, contact us and we will delete it.',
        ],
      },
      {
        heading: '7. Children\'s Privacy',
        paragraphs: [
          'The Service is not intended for children under 16. We do not knowingly collect personal information from children under 16.',
        ],
      },
      {
        heading: '8. Changes to This Policy',
        paragraphs: [
          'We may update this Privacy Policy from time to time. We will notify you of material changes by displaying a notice in the app. Continued use after changes constitutes acceptance.',
        ],
      },
      {
        heading: '9. Contact',
        paragraphs: [
          'For privacy questions or data requests, contact privacy@propermiles.com.',
        ],
      },
    ],
  },

  safety: {
    title: 'Safety Disclaimer',
    sections: [
      {
        heading: 'Important Safety Notice',
        paragraphs: [
          'PLEASE READ THIS SAFETY DISCLAIMER CAREFULLY BEFORE USING ANY DIY REPAIR GUIDE OR MAINTENANCE RECOMMENDATION PROVIDED BY PROPER MILES MOBILE GARAGE.',
        ],
      },
      {
        heading: 'AI-Generated Content Warning',
        paragraphs: [
          'All repair guides, maintenance instructions, torque specifications, part numbers, and procedural steps provided by this application are generated by artificial intelligence and may contain errors, omissions, or inaccuracies.',
          'AI-generated content should be treated as a starting reference only. Always verify critical specifications (torque values, fluid types, part numbers) against your vehicle\'s official service manual or a qualified mechanic before proceeding.',
        ],
      },
      {
        heading: 'Personal Safety',
        paragraphs: [
          'Vehicle repair involves serious risks including but not limited to: crushing injuries from improperly supported vehicles, burns from hot components or fluids, chemical exposure from automotive fluids, electrical shock, eye injuries, and other hazards.',
          'Always use proper personal protective equipment (PPE) including safety glasses, gloves, and appropriate clothing. Never work under a vehicle supported only by a jack. Use proper jack stands rated for your vehicle\'s weight.',
          'If you are unsure about any step in a repair procedure, STOP and consult a qualified professional mechanic. No cost savings are worth risking your safety.',
        ],
      },
      {
        heading: 'Vehicle Safety',
        paragraphs: [
          'Improperly performed repairs can result in vehicle malfunction, brake failure, loss of steering control, fire, or other dangerous conditions that could cause serious injury or death to you or others.',
          'After performing any brake, steering, suspension, or safety-critical repair, test the vehicle at low speed in a safe area before normal driving. If anything feels wrong, do not drive the vehicle — have it towed to a professional.',
        ],
      },
      {
        heading: 'Limitation of Responsibility',
        paragraphs: [
          'Proper Miles Inc. is not responsible for any injuries, damages, or losses resulting from the use of information provided by this application. You perform all repairs at your own risk.',
          'By using the DIY repair guides, you acknowledge that you have the necessary skills, tools, and workspace to safely perform the described procedures, and that you accept full responsibility for the outcome.',
        ],
      },
      {
        heading: 'When to Seek Professional Help',
        paragraphs: [
          'We strongly recommend professional service for: airbag systems, fuel system repairs, transmission rebuilds, engine internal work, advanced electrical diagnostics, any repair you are not confident performing, and any situation where safety is in doubt.',
        ],
      },
    ],
  },

  events: {
    title: 'Event Liability Disclaimer',
    sections: [
      {
        heading: 'Important Notice',
        paragraphs: [
          'PLEASE READ THIS DISCLAIMER CAREFULLY. The Proper Miles Mobile Garage platform ("Platform") facilitates the discovery of and registration for automotive-related events organized by independent clubs and organizations. Proper Miles LLC does not operate, organize, manage, or control any third-party events listed on the Platform.',
        ],
      },
      {
        heading: 'Third-Party Events',
        paragraphs: [
          'Events listed on the Platform by clubs or organizations other than Proper Miles Drivers Club are created, managed, and conducted entirely by those independent organizations ("Event Organizers"). Proper Miles LLC does not vet, endorse, verify, inspect, or supervise these events, their organizers, their venues, or their activities.',
          'Proper Miles LLC has no control over and assumes no responsibility for the content, safety measures, insurance coverage, legality, or conduct of any third-party event. Your decision to attend any event is made entirely at your own risk and discretion.',
        ],
      },
      {
        heading: 'Assumption of Risk',
        paragraphs: [
          'Automotive events may involve inherent and significant risks, including but not limited to: vehicle operation and driving hazards, mechanical failure, road and weather conditions, the actions of other participants and attendees, physical activities, and exposure to loud noise, exhaust fumes, and other environmental conditions.',
          'BY REGISTERING FOR OR ATTENDING ANY EVENT DISCOVERED THROUGH THE PLATFORM, YOU VOLUNTARILY ASSUME ALL RISKS OF INJURY, ILLNESS, DEATH, OR PROPERTY DAMAGE THAT MAY RESULT FROM YOUR PARTICIPATION OR ATTENDANCE, WHETHER CAUSED BY NEGLIGENCE OR OTHERWISE.',
        ],
      },
      {
        heading: 'Limitation of Liability',
        paragraphs: [
          'TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PROPER MILES LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, AND PARTNERS SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO PERSONAL INJURY, DEATH, PROPERTY DAMAGE, LOSS OF USE, LOSS OF DATA, OR LOSS OF PROFITS, ARISING FROM OR RELATED TO ANY EVENT LISTED ON THE PLATFORM.',
          'This limitation of liability applies regardless of whether the event is organized by a third-party club or by Proper Miles Drivers Club, and regardless of the legal theory upon which the claim is based, including warranty, contract, tort, negligence, strict liability, or any other basis.',
        ],
      },
      {
        heading: 'No Warranties Regarding Events',
        paragraphs: [
          'PROPER MILES LLC MAKES NO WARRANTIES OR REPRESENTATIONS, EXPRESS OR IMPLIED, REGARDING ANY EVENT LISTED ON THE PLATFORM, INCLUDING BUT NOT LIMITED TO: the safety, quality, or organization of any event; the qualifications, licensing, or insurance coverage of any Event Organizer; the accuracy of event descriptions, schedules, or other information provided by Event Organizers; or compliance with applicable laws, regulations, or permits.',
        ],
      },
      {
        heading: 'Proper Miles Drivers Club Events',
        paragraphs: [
          'For events organized specifically by Proper Miles Drivers Club, additional terms and conditions may apply. Please review the full participation waiver and release of liability at propermiles.com/waiver before attending any Proper Miles Drivers Club event.',
          'Participation in Proper Miles Drivers Club events may require execution of that waiver as a condition of attendance. The waiver contains important terms regarding your rights and responsibilities.',
        ],
      },
      {
        heading: 'Indemnification',
        paragraphs: [
          'You agree to indemnify, defend, and hold harmless Proper Miles LLC, its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys\' fees) arising from or related to: (a) your attendance at or participation in any event discovered through the Platform; (b) your violation of this Disclaimer or any applicable law; or (c) your negligent or wrongful conduct at any event.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'For questions about this Event Liability Disclaimer, contact legal@propermiles.com.',
        ],
      },
    ],
  },
};
